import type { VirtualClinicAnalysisOperatingNumberGroupBy } from './group-by';
import type { VirtualClinicAnalysisOperatingNumberResultNode } from '../result/result-node';

export class VirtualClinicAnalysisOperatingNumberGroupBySortHelper {
  public static getSortByGroupLabelFnc(
    groupBy: VirtualClinicAnalysisOperatingNumberGroupBy,
    reverse: -1 | 1,
  ): (
    a: VirtualClinicAnalysisOperatingNumberResultNode,
    b: VirtualClinicAnalysisOperatingNumberResultNode,
  ) => number {
    let sortFnc;

    const timeUnitFields = ['admissionDateTime', 'dismissalDateTime'];
    if (timeUnitFields.includes(groupBy.fieldName)) {
      sortFnc = this._getSortByTimeUnitGroupLabelFnc(groupBy, reverse);
    } else if (groupBy.fieldName === 'agmId') {
      sortFnc = this._getSortAgeGroupLabelFnc(groupBy, reverse);
    } else if (groupBy.fieldName === 'drivingTime') {
      sortFnc = this._getSortDrivingTimeLabelFnc(groupBy, reverse);
    }

    return sortFnc ?? this._getDefaultSortByGroupLabelFnc(reverse);
  }

  private static _getSortDrivingTimeLabelFnc(
    groupBy: VirtualClinicAnalysisOperatingNumberGroupBy,
    reverse: -1 | 1,
  ):
    | ((
        a: VirtualClinicAnalysisOperatingNumberResultNode,
        b: VirtualClinicAnalysisOperatingNumberResultNode,
      ) => number)
    | null {
    const sortFnc = (
      a: VirtualClinicAnalysisOperatingNumberResultNode,
      b: VirtualClinicAnalysisOperatingNumberResultNode,
    ) => {
      const sorter = {
        '0-10': 10,
        '10-20': 20,
        '20-30': 30,
        '30-40': 40,
        '40-50': 50,
        '50-60': 60,
        '60-70': 70,
        '70-80': 80,
        '80-90': 90,
        '90-100': 100,
        '100-110': 110,
        '>250': 250,
      };

      return reverse * (sorter[a.label.key] - sorter[b.label.key]);
    };

    return sortFnc;
  }

  private static _getSortAgeGroupLabelFnc(
    groupBy: VirtualClinicAnalysisOperatingNumberGroupBy,
    reverse: -1 | 1,
  ):
    | ((
        a: VirtualClinicAnalysisOperatingNumberResultNode,
        b: VirtualClinicAnalysisOperatingNumberResultNode,
      ) => number)
    | null {
    const sortFnc = (
      a: VirtualClinicAnalysisOperatingNumberResultNode,
      b: VirtualClinicAnalysisOperatingNumberResultNode,
    ) => {
      const sorter = {
        '00 - < 01': 0,
        '01 - < 05': 1,
        '05 - < 10': 2,
        '10 - < 15': 3,
        '15 - < 20': 4,
        '20 - < 25': 5,
        '25 - < 30': 6,
        '30 - < 35': 7,
        '35 - < 40': 8,
        '40 - < 45': 9,
        '45 - < 50': 10,
        '50 - < 55': 11,
        '55 - < 60': 12,
        '60 - < 65': 13,
        '65 - < 70': 14,
        '70 - < 75': 15,
        '75 - < 80': 16,
        '80 - < 85': 17,
        '85 - < 90': 18,
        '90 - < 95': 19,
        '> 95': 20,
        unbekannt: 21,
      };

      return (
        reverse *
        (sorter[a.label.shortDisplayName] - sorter[b.label.shortDisplayName])
      );
    };

    return sortFnc;
  }

  private static _getSortByTimeUnitGroupLabelFnc(
    groupBy: VirtualClinicAnalysisOperatingNumberGroupBy,
    reverse: -1 | 1,
  ):
    | ((
        a: VirtualClinicAnalysisOperatingNumberResultNode,
        b: VirtualClinicAnalysisOperatingNumberResultNode,
      ) => number)
    | null {
    const parameterValues =
      groupBy.parameters?.map((parameter) => parameter.value) ?? [];

    // Time-unit groupings expose German name labels (weekday / month names) that
    // must be ordered by their calendar position, not alphabetically. Each entry
    // maps the parameter value to the ordinal sorter for its `shortDisplayName`.
    const calendarOrderByParameterValue: {
      [parameterValue: string]: { [shortDisplayName: string]: number };
    } = {
      dayOfWeek: {
        Montag: 1,
        Dienstag: 2,
        Mittwoch: 3,
        Donnerstag: 4,
        Freitag: 5,
        Samstag: 6,
        Sonntag: 7,
      },
      monthOfYear: {
        Januar: 1,
        Februar: 2,
        März: 3,
        April: 4,
        Mai: 5,
        Juni: 6,
        Juli: 7,
        August: 8,
        September: 9,
        Oktober: 10,
        November: 11,
        Dezember: 12,
      },
    };

    const parameterValue = parameterValues.find(
      (value) => calendarOrderByParameterValue[value] !== undefined,
    );

    if (parameterValue === undefined) {
      return null;
    }

    const sorter = calendarOrderByParameterValue[parameterValue];

    return (
      a: VirtualClinicAnalysisOperatingNumberResultNode,
      b: VirtualClinicAnalysisOperatingNumberResultNode,
    ) =>
      reverse *
      (sorter[a.label.shortDisplayName] - sorter[b.label.shortDisplayName]);
  }

  private static _getDefaultSortByGroupLabelFnc(
    reverse: -1 | 1,
  ): (
    a: VirtualClinicAnalysisOperatingNumberResultNode,
    b: VirtualClinicAnalysisOperatingNumberResultNode,
  ) => number {
    return (a, b) => {
      return (
        reverse *
        a.label.shortDisplayName.localeCompare(b.label.shortDisplayName)
      );
    };
  }
}
